import React, { useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, FileText } from "lucide-react";
import { FileAzioni } from "./FileViewer";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { statoBusta, calcRitardo, BusteTiles } from "./BustePaga";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

export default function BustePagaRO({ opId, dataAssunzione }) {
  const assunzione = (dataAssunzione || "").slice(0, 10);
  const qc = useQueryClient();
  const [annoSel, setAnnoSel] = useState(null);
  const fileRefs = useRef({});
  const { data: buste = [] } = useQuery({
    queryKey: ["mio-buste", opId], enabled: !!opId, retry: false,
    queryFn: async () => (await api.get("/firma/mio-account/buste", { params: { op_id: opId } })).data,
  });
  const anni = Array.from(new Set(buste.map((b) => b.anno).filter(Boolean))).sort((a, b) => b - a);
  const yr = annoSel || anni[0] || new Date().getFullYear();
  const byMese = {}; buste.filter((b) => b.anno === yr).forEach((b) => { byMese[b.mese] = b; });
  const rit = calcRitardo(buste, assunzione);

  const upload = async (mese, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("anno", yr); fd.append("mese", mese);
    try { await api.post(`/firma/mio-account/buste?op_id=${opId}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Busta paga caricata"); qc.invalidateQueries({ queryKey: ["mio-buste", opId] }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Errore caricamento"); }
  };

  return (
    <div data-testid="mio-buste">
      <div className="flex items-center gap-2 mb-3">
        <FileText size={18} className="text-primary" />
        <h3 className="font-head font-bold">Le mie buste paga</h3>
      </div>
      <BusteTiles assunzione={assunzione} {...rit} />
      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm font-semibold">Anno</label>
        <select value={yr} onChange={(e) => setAnnoSel(Number(e.target.value))} className="h-10 rounded-md border px-3 text-sm font-semibold" data-testid="mio-buste-anno">
          {(Array.from(new Set([...anni, yr, new Date().getFullYear()])).sort((a, b) => b - a)).map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{buste.filter((b) => b.anno === yr).length} buste nel {yr}</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {MESI.map((nome, idx) => { const mese = idx + 1; const b = byMese[mese]; const s = statoBusta(yr, mese, b, assunzione); const off = s.k === "prima" || s.k === "futuro"; return (
          <div key={mese} className={`rounded-xl border-2 bg-white p-3 flex items-center justify-between gap-2 ${off ? "opacity-60" : ""}`} style={{ borderColor: `${s.c}55` }} data-testid={`mio-busta-mese-${mese}`}>
            <div className="flex items-center gap-2 min-w-0">
              <FileText size={18} style={{ color: s.c }} className="shrink-0" />
              <div className="min-w-0">
                <div className="font-semibold text-sm flex items-center gap-1.5 flex-wrap">{nome}{s.l && <span className="text-[10px] font-bold rounded-full px-1.5 py-0.5" style={s.k === "ritardo" ? { background: s.c, color: "#fff" } : { background: `${s.c}18`, color: s.c }} data-testid={`mio-busta-stato-${mese}`}>{s.l}</span>}</div>
                <div className="text-[11px] text-muted-foreground truncate">{b ? (b.original_filename || "disponibile") : (s.sub || "Non disponibile")}</div>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {b ? (
                <>
                  <FileAzioni url={`/firma/mio-account/buste/${b.id}/download`} params={{ op_id: opId }} titolo={b.original_filename || `busta_${yr}_${b.mese}.pdf`} testid={`mio-busta-${mese}`} size={16} />
                </>
              ) : off ? null : (
                <>
                  <input ref={(el) => (fileRefs.current[mese] = el)} type="file" accept=".pdf,image/*" className="hidden" data-testid={`mio-busta-file-${mese}`} onChange={(e) => upload(mese, e.target.files?.[0])} />
                  <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => fileRefs.current[mese]?.click()} data-testid={`mio-busta-upload-${mese}`}><Upload size={14} className="mr-1" /> Carica</Button>
                </>
              )}
            </div>
          </div>
        ); })}
      </div>
    </div>
  );
}
